import { useCallback, useEffect, useState } from 'react'
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  type AppNotification,
} from '@/services/notificationService'
import { useApiSessionReady } from '@/hooks/useApiSessionReady'

const POLL_MS = 60_000

export function useNotifications() {
  const sessionReady = useApiSessionReady()
  const [unreadCount, setUnreadCount] = useState(0)
  const [items, setItems] = useState<AppNotification[]>([])
  const [loading, setLoading] = useState(false)

  const refreshUnreadCount = useCallback(async () => {
    try {
      const count = await fetchUnreadCount()
      setUnreadCount(count)
    } catch {
      /* silencioso — badge opcional */
    }
  }, [])

  const loadNotifications = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchNotifications({ limit: 50 })
      setItems(res.data ?? [])
      await refreshUnreadCount()
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [refreshUnreadCount])

  const markAsRead = useCallback(
    async (id: string) => {
      await markNotificationAsRead(id)
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, lida: true } : n)))
      setUnreadCount((c) => Math.max(0, c - 1))
    },
    [],
  )

  const markAllAsRead = useCallback(async () => {
    await markAllNotificationsAsRead()
    setItems((prev) => prev.map((n) => ({ ...n, lida: true })))
    setUnreadCount(0)
  }, [])

  useEffect(() => {
    if (!sessionReady) return
    refreshUnreadCount()
    const timer = window.setInterval(refreshUnreadCount, POLL_MS)
    return () => window.clearInterval(timer)
  }, [refreshUnreadCount, sessionReady])

  return {
    unreadCount,
    items,
    loading,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    refreshUnreadCount,
  }
}
